'use client'

import { LineChevronLeftSmall } from '@qr-front/icons/line-chevron-left-small'
import { LineChevronRightSmall } from '@qr-front/icons/line-chevron-right-small'
import useEmblaCarousel, {
	type UseEmblaCarouselType,
} from 'embla-carousel-react'
import * as React from 'react'
import { cn } from 'utils/cn'

import { Button } from './Button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
	opts?: CarouselOptions
	plugins?: CarouselPlugin
	orientation?: 'horizontal' | 'vertical'
	setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0]
	api: ReturnType<typeof useEmblaCarousel>[1]
	scrollPrev: () => void
	scrollNext: () => void
	canScrollPrev: boolean
	canScrollNext: boolean
} & CarouselProps

interface CarouselDotsProps {
	current: number
	count: number
	classNames?: string
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

const useCarousel = () => {
	const context = React.useContext(CarouselContext)

	if (!context) {
		throw new Error('useCarousel must be used within a <Carousel />')
	}

	return context
}

const useCarouselState = (api: CarouselApi | null) => {
	const [state, setState] = React.useState<{
		current: number
		count: number
	}>({
		current: 0,
		count: 0,
	})

	React.useEffect(() => {
		if (!api) return

		const update = () =>
			setState({
				current: api.selectedScrollSnap() + 1,
				count: api.scrollSnapList().length,
			})

		update()
		api.on('select', update)
		api.on('reInit', update)

		return () => {
			api.off('select', update)
			api.off('reInit', update)
		}
	}, [api])

	return state
}

const Carousel = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
	(
		{
			orientation = 'horizontal',
			opts,
			setApi,
			plugins,
			className,
			children,
			...props
		},
		ref,
	) => {
		const [carouselRef, api] = useEmblaCarousel(
			{
				...opts,
				axis: orientation === 'horizontal' ? 'x' : 'y',
			},
			plugins,
		)
		const [canScrollPrev, setCanScrollPrev] = React.useState(false)
		const [canScrollNext, setCanScrollNext] = React.useState(false)

		const onSelect = React.useCallback((api: CarouselApi) => {
			if (!api) {
				return
			}

			setCanScrollPrev(api.canScrollPrev())
			setCanScrollNext(api.canScrollNext())
		}, [])

		const scrollPrev = React.useCallback(() => {
			api?.scrollPrev()
		}, [api])

		const scrollNext = React.useCallback(() => {
			api?.scrollNext()
		}, [api])

		const handleKeyDown = React.useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'ArrowLeft') {
					event.preventDefault()
					scrollPrev()
				} else if (event.key === 'ArrowRight') {
					event.preventDefault()
					scrollNext()
				}
			},
			[scrollPrev, scrollNext],
		)

		React.useEffect(() => {
			if (!api || !setApi) {
				return
			}

			setApi(api)
		}, [api, setApi])

		React.useEffect(() => {
			if (!api) {
				return
			}

			onSelect(api)
			api.on('reInit', onSelect)
			api.on('select', onSelect)

			return () => {
				api?.off('select', onSelect)
			}
		}, [api, onSelect])

		return (
			<CarouselContext.Provider
				value={{
					carouselRef,
					api: api,
					opts,
					orientation:
						orientation ||
						(opts?.axis === 'y' ? 'vertical' : 'horizontal'),
					scrollPrev,
					scrollNext,
					canScrollPrev,
					canScrollNext,
				}}
			>
				<div
					ref={ref}
					onKeyDownCapture={handleKeyDown}
					className={cn('relative', className)}
					role="region"
					aria-roledescription="carousel"
					{...props}
				>
					{children}
				</div>
			</CarouselContext.Provider>
		)
	},
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	const { carouselRef, orientation } = useCarousel()

	return (
		<div ref={carouselRef} className="overflow-hidden">
			<div
				ref={ref}
				className={cn(
					'flex',
					orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
					className,
				)}
				{...props}
			/>
		</div>
	)
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	const { orientation } = useCarousel()

	return (
		<div
			ref={ref}
			role="group"
			aria-roledescription="slide"
			className={cn(
				'min-w-0 shrink-0 grow-0 basis-full',
				orientation === 'horizontal' ? 'pl-4' : 'pt-4',
				className,
			)}
			{...props}
		/>
	)
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof Button> & { iconClassName?: string }
>(({ className, iconClassName, size = 'icon', visual, ...props }, ref) => {
	const { scrollPrev, canScrollPrev } = useCarousel()

	return (
		<Button
			ref={ref}
			size={size}
			visual={visual}
			className={cn(
				'h-8 w-8 rounded-full desktop:h-10 desktop:w-10',
				!visual && 'bg-dark-grey hover:bg-black',
				className,
			)}
			disabled={!canScrollPrev}
			onClick={scrollPrev}
			{...props}
		>
			<LineChevronLeftSmall
				className={cn('h-6 w-6 text-white', iconClassName)}
			/>
			<span className="sr-only">Previous slide</span>
		</Button>
	)
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof Button> & { iconClassName?: string }
>(({ className, iconClassName, size = 'icon', visual, ...props }, ref) => {
	const { scrollNext, canScrollNext } = useCarousel()

	return (
		<Button
			ref={ref}
			size={size}
			visual={visual}
			className={cn(
				'h-8 w-8 rounded-full desktop:h-10 desktop:w-10',
				!visual && 'bg-dark-grey hover:bg-black',
				className,
			)}
			disabled={!canScrollNext}
			onClick={scrollNext}
			{...props}
		>
			<LineChevronRightSmall
				className={cn('h-6 w-6 text-white', iconClassName)}
			/>
			<span className="sr-only">Next slide</span>
		</Button>
	)
})
CarouselNext.displayName = 'CarouselNext'

const CarouselButtons = ({
	className,
	buttonClassName,
}: {
	className?: string
	buttonClassName?: string
}) => {
	return (
		<div
			className={cn(
				'relative mt-8 hidden w-full justify-center gap-4 desktop:flex',
				className,
			)}
		>
			<CarouselPrevious className={buttonClassName} />
			<CarouselNext className={buttonClassName} />
		</div>
	)
}

const CarouselDots = ({
	current,
	count,
	onClick,
	classNames,
	...props
}: CarouselDotsProps & { onClick?: (index: number) => void }) => {
	return (
		<ul
			className={cn(classNames, 'flex justify-center gap-1')}
			aria-label="Carousel navigation"
			role="list"
		>
			{Array.from({ length: count }).map((_, index) => (
				<li
					key={index}
					{...props}
					aria-label={`Slide ${current} of ${count}`}
					role="listitem"
					className="leading-none"
				>
					<button
						onClick={onClick ? () => onClick(index) : undefined}
						className={cn(
							'h-1.5 w-1.5 rounded-full bg-border-grey transition',
							index === current - 1 && 'w-3 bg-blue',
						)}
					/>
				</li>
			))}
		</ul>
	)
}

CarouselDots.displayName = 'CarouselDots'

export {
	Carousel,
	type CarouselApi,
	CarouselButtons,
	CarouselContent,
	CarouselDots,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	useCarousel,
	useCarouselState,
}
